import { type FC, useCallback, useMemo } from 'react';

import { AccommodationPlaceholder } from '@vakantiesnl/components/src/atoms/AccommodationPlaceholder';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AvailabilityCheckModal } from '@vakantiesnl/components/src/blocks/accommodation/availability-check-modal';
import { AccoOfferSummary } from '@vakantiesnl/components/src/molecules/OfferSummary/AccoOfferSummary';
import { OfferSummaryWrapper } from '@vakantiesnl/components/src/molecules/OfferSummaryWrapper';
import { PriceTable } from '@vakantiesnl/components/src/molecules/PriceTable';
import { getBoardDescription, getRoomsDescription } from '@vakantiesnl/components/src/utils/accommodation';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAccommodationContext } from '@vakantiesnl/services/src/context/useAccommodationContext';
import {
	RequestType,
	useAvailabilityCheckContext,
} from '@vakantiesnl/services/src/context/useAvailabilityCheckContext';
import { buildAvailabilityRequestBody } from '@vakantiesnl/services/src/dynamic-booking/build';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { usePartyComposition } from '@vakantiesnl/services/src/stores/globalFiltersStore';
import { type Search } from '@vakantiesnl/types';

import { useCustomStyles } from './price-overview.styles';

const PriceOverview: FC = () => {
	const { isDesktop } = useBreakpoints();
	const {
		api: { setAvailabilityRequestBody },
		state: availabilityState,
	} = useAvailabilityCheckContext();
	const { checking } = availabilityState;
	const microCopies = useMicroCopyContext();
	const { accommodation } = useGetAccommodationByEntityId();
	const {
		state: { priceOverviewData, roomData, flightData },
	} = useAccommodationContext();
	const rooms = usePartyComposition();
	const { classes } = useCustomStyles();

	const selectedOffer = priceOverviewData?.selected;

	const partyComposition: Search.Party | undefined = useMemo(
		() => (priceOverviewData?.party ? priceOverviewData?.party : undefined),
		[priceOverviewData?.party],
	);

	const body =
		selectedOffer && partyComposition
			? buildAvailabilityRequestBody(selectedOffer, accommodation, partyComposition, rooms, microCopies)
			: undefined;

	const checkAvailability = useCallback(() => {
		const oldPrice = selectedOffer?.priceTotal;
		setAvailabilityRequestBody(
			oldPrice && body
				? {
						requestBody: body,
						requestType: RequestType.OFFER_AVAILABILITY,
						oldPrice,
					}
				: undefined,
		);
	}, [setAvailabilityRequestBody, body, selectedOffer]);

	const priceOverview = useCallback(() => {
		return selectedOffer && partyComposition ? (
			<PriceTable
				microCopies={microCopies}
				pricePerAdult={selectedOffer.pricePerAdult}
				pricePerBaby={selectedOffer.pricePerBaby}
				pricePerChild={selectedOffer.pricePerChild}
				funds={selectedOffer.extras.funds.items}
				totalParty={partyComposition}
				handLuggageCostsIncluded={false}
				bookingCosts={selectedOffer.extras.surcharges.items}
				/** Not showing if the booking costs are included on the acco page, only in IBE */
				hideBookingCostsIncluded={true}
				partyPerRooms={rooms}
				priceTotal={selectedOffer.priceTotal}
			/>
		) : null;
	}, [microCopies, partyComposition, rooms, selectedOffer]);

	const bookingSummary = useCallback(
		(withPriceOverview?: boolean) => {
			return (
				partyComposition &&
				selectedOffer && (
					<AccoOfferSummary
						checkAvailability={checkAvailability}
						isCheckingAvailability={checking}
						microCopies={microCopies}
						accommodation={accommodation}
						outboundFlight={selectedOffer.outbound}
						inboundFlight={selectedOffer.inbound}
						mealplanDescription={getBoardDescription(selectedOffer, microCopies)}
						roomDescription={getRoomsDescription(selectedOffer.rooms, microCopies)}
						party={partyComposition}
						hasTransfer={selectedOffer.hasTransferIncluded}
						hasLuggageIncluded={selectedOffer.hasLuggageIncluded}
						durationNights={selectedOffer.durationNights}
						durationDays={selectedOffer.durationDays}
						withPriceOverview={withPriceOverview}
						tourOperator={selectedOffer?.tourOperator}
						departureDate={selectedOffer.departureDateYYYYMMDD}
					>
						{withPriceOverview && priceOverview()}
					</AccoOfferSummary>
				)
			);
		},
		[partyComposition, selectedOffer, checkAvailability, checking, microCopies, accommodation, priceOverview],
	);

	const onSummaryTabChangeHandler = useCallback(
		(_index: number, tabCaption?: string | undefined) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ offerOverviewTabSwitch }) =>
					track(offerOverviewTabSwitch(tabCaption ?? null, accommodation, 'step5_block')),
				);
			});
		},
		[accommodation],
	);

	return (
		<div data-cy="price-overview">
			<Typography variant={isDesktop ? 'headingXl' : 'headingMd'} as="h3" className={classes.title}>
				{microCopies['accommodation.booking_summary.title']}
			</Typography>
			{priceOverviewData !== undefined && priceOverviewData.party && selectedOffer ? (
				<>
					{!isDesktop ? (
						<OfferSummaryWrapper
							tripOverviewChildren={bookingSummary(false)}
							priceOverviewChildren={priceOverview()}
							checkAvailability={checkAvailability}
							isCheckingAvailability={checking}
							onTabChange={onSummaryTabChangeHandler}
							isTabView={true}
							tourOperator={selectedOffer.tourOperator}
						/>
					) : (
						bookingSummary(true)
					)}
				</>
			) : (
				<AccommodationPlaceholder title={microCopies['accommodation.booking_summary.placeholder_text']} />
			)}
			<AvailabilityCheckModal microCopy={microCopies} roomData={roomData} flightData={flightData} />
		</div>
	);
};

export default PriceOverview;
